<template>
  <layout-basic>
    <page-hero
      title="Is Your Learning Platform Holding You Back?"
      lead="The right learning platform should make training easier—not more complicated."
      eyebrow="Why Switch"
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video video-key="compare" />
      </v-container>
    </section>

    <section class="why-intro">
      <v-container style="max-width: 720px">
        <p>
          If you're spending too much time managing training, struggling to engage
          employees or paying for features you never use, it may be time for a
          different approach.
        </p>
      </v-container>
    </section>

    <point-grid
      title="It might be time to switch if..."
      :points="switchIfPoints"
      :columns="2"
      tint
    />

    <section class="why-choose">
      <v-container style="max-width: 860px">
        <h2 class="font-sora section-heading">Why organisations choose IMPROVE</h2>
        <p class="why-choose__lead">
          Growing businesses switch to IMPROVE because they want a learning platform
          that's simple, practical and built around real business needs.
        </p>
        <p class="why-choose__sub">With IMPROVE you can:</p>
        <ul class="why-list">
          <li v-for="(item, i) in choosePoints" :key="i">
            <span class="why-list__mark" aria-hidden="true">✔</span>
            <span>{{ item }}</span>
          </li>
        </ul>
      </v-container>
    </section>

    <section class="why-migrate">
      <v-container style="max-width: 720px">
        <h2 class="font-sora section-heading">Thinking of switching?</h2>
        <p>
          Moving to a new learning platform doesn't have to be difficult. Our team
          will help you transfer your learning, support your implementation and get
          your organisation up and running quickly.
        </p>
        <p>
          IMPROVE is built for SMEs (typically 5–250), with upload-your-own content,
          130+ courses, simple annual pricing per business, and Leadership Plus as an
          optional bolt-on when you’re ready to develop managers further.
        </p>
      </v-container>
    </section>

    <section class="why-actions">
      <v-container style="max-width: 980px">
        <h2 class="font-sora section-heading">Ready to see the difference?</h2>
        <div class="why-actions__grid">
          <component
            :is="watchIsExternal ? 'a' : 'router-link'"
            v-bind="watchBind"
            class="why-action-card"
          >
            <strong>▶ {{ CTA.watchDemo.label }}</strong>
            <span>{{ CTA.watchDemo.description }}</span>
          </component>
          <a
            :href="experienceHref"
            class="why-action-card"
            rel="noopener"
          >
            <strong>▶ {{ CTA.experience.label }}</strong>
            <span>{{ CTA.experience.description }}</span>
          </a>
          <router-link to="/compare" class="why-action-card">
            <strong>▶ Compare IMPROVE</strong>
            <span>
              See how IMPROVE compares with other popular learning platforms before
              making your decision.
            </span>
          </router-link>
        </div>
        <p class="why-actions__more">
          Or explore
          <router-link to="/compare">all comparisons</router-link>
          and
          <router-link to="/get-started">buy IMPROVE</router-link>
          when you’re ready.
        </p>
      </v-container>
    </section>

    <standard-cta
      title="Make the switch with confidence."
      lead="See why growing businesses choose IMPROVE to onboard, train and develop their people."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { CTA, getExperienceHref } from '@/config/ctas';
import { marketingPageMeta } from '@/utils/marketingPage';

export default {
  name: 'WhySwitch',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Is Your Learning Platform Holding You Back? | IMPROVE',
      description:
        'If onboarding takes too long, compliance is hard to manage, or your LMS feels complicated, it may be time to switch to IMPROVE.',
      path: '/why-switch',
    });
  },
  data() {
    return {
      CTA,
      switchIfPoints: [
        'Onboarding new employees takes too long',
        'Compliance training is difficult to manage',
        'Your learning platform feels complicated',
        "Employees aren't engaging with training",
        "You can't easily upload your own content",
        "You're paying for functionality you don't need",
      ],
      choosePoints: [
        'Onboard new employees faster',
        'Keep compliance training organised',
        'Upload your own learning content',
        'Develop managers and employees in one platform',
        'Give every employee access to engaging online learning',
        'Enjoy simple, transparent pricing designed for growing businesses',
      ],
    };
  },
  computed: {
    experienceHref() {
      return getExperienceHref();
    },
    watchIsExternal() {
      return /^https?:\/\//.test(CTA.watchDemo.href);
    },
    watchBind() {
      return this.watchIsExternal
        ? { href: CTA.watchDemo.href }
        : { to: CTA.watchDemo.href };
    },
  },
};
</script>

<style scoped>
.why-intro {
  padding: 8px 0 16px;
  text-align: center;
}

.why-intro p {
  color: var(--brand-grey, #6b7280);
  font-size: 17px;
  line-height: 1.6;
  margin: 0;
}

.section-heading {
  text-align: center;
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 16px;
}

.why-choose {
  padding: 40px 0 24px;
}

.why-choose__lead,
.why-choose__sub,
.why-migrate p {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
  margin: 0 0 16px;
}

.why-choose__sub {
  font-weight: 600;
  color: var(--brand-navy, #002458);
  margin-bottom: 12px;
}

.why-list {
  list-style: none;
  padding: 0;
  margin: 0 auto;
  max-width: 640px;
  display: grid;
  gap: 10px;
}

.why-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 10px;
  padding: 14px 16px;
  color: var(--brand-navy, #002458);
  font-weight: 600;
  font-size: 15px;
  line-height: 1.4;
}

.why-list__mark {
  color: var(--brand-teal, #08a0a8);
  flex: 0 0 auto;
}

.why-migrate {
  padding: 24px 0 40px;
  background: #f7f9fb;
}

.why-actions {
  padding: 48px 0 40px;
}

.why-actions__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.why-action-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
  text-decoration: none;
  color: var(--brand-navy, #002458);
  box-shadow: 0 8px 24px rgba(0, 36, 88, 0.06);
  transition: border-color 0.15s ease;
}

.why-action-card:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-navy, #002458);
}

.why-action-card strong {
  font-size: 17px;
}

.why-action-card span {
  font-size: 14px;
  line-height: 1.45;
  color: var(--brand-grey, #6b7280);
}

.why-actions__more {
  text-align: center;
  margin: 24px 0 0;
  color: var(--brand-grey, #6b7280);
  font-size: 15px;
}

.why-actions__more a {
  color: var(--brand-teal, #08a0a8);
  font-weight: 700;
  text-decoration: none;
}

.why-actions__more a:hover {
  text-decoration: underline;
}

@media screen and (max-width: 900px) {
  .why-actions__grid {
    grid-template-columns: 1fr;
  }

  .section-heading {
    font-size: 24px;
  }
}
</style>
